import React from 'react';
import { useApp } from '@/contexts/AppContext';
import { motion } from 'framer-motion';
import { CheckCircle2, MinusCircle, Clock, GitGraph, Cloud } from 'lucide-react';
import IntegrationsTimeline from '@/components/apex/IntegrationsTimeline';
import EnterpriseReadiness from '@/components/apex/EnterpriseReadiness';

const STATUS = {
  connected: { icon: CheckCircle2, color: 'text-apex-success', bg: 'border-emerald-200 bg-emerald-50/40', label: 'CONNECTED' },
  not_connected: { icon: MinusCircle, color: 'text-slate-400', bg: 'border-slate-200 bg-slate-50/40', label: 'NOT CONNECTED' },
};

const PILOT_COLUMNS = [
  { label: 'What you provide', items: ['Read-only AWS account access', 'CloudTrail enabled (90 days+)', '2–4 hours over 30 days'] },
  { label: 'What you receive', items: ['First governance evidence in 3 days', 'Identity proof report at day 7', 'Full audit packet at day 14', 'Governance debt baseline at day 30'] },
  { label: 'What it costs', items: ['Zero during the 90-day pilot', 'No agents, no infrastructure changes', 'Revokable at any time', 'Data stays in your AWS account'] },
];

// Cloud provider maturity — single-source-of-truth on what works today vs. roadmap.
const PROVIDER_MATURITY = [
  {
    id: 'aws',
    name: 'Amazon Web Services',
    stage: 'GA',
    stageTone: 'success',
    coverage: 100,
    headline: 'Foundational evidence sources live across all 9 connector types.',
    highlights: [
      'CloudTrail (mgmt + data events) · AWS Config · GuardDuty · Macie · Inspector',
      'IAM Access Analyzer · ECR · Athena reconstruction · AWS Organizations',
      'Cross-account read-only role · zero infrastructure changes',
    ],
    note: 'Production-ready since day one. Powers every page in this demo.',
  },
  {
    id: 'azure',
    name: 'Microsoft Azure',
    stage: 'Roadmap · Q3 2026',
    stageTone: 'warning',
    coverage: 35,
    headline: 'Connector design complete. Pilot integrations begin Q3 2026.',
    highlights: [
      'Activity Log · Defender for Cloud · Microsoft Sentinel mappings drafted',
      'Azure AD identity proof reuses the AWS IAM Access Analyzer model',
      'Resource Graph used for evidence reconstruction (parity with Athena)',
    ],
    note: 'Design partners with Azure footprints can shape the connector schema.',
  },
  {
    id: 'gcp',
    name: 'Google Cloud Platform',
    stage: 'Planned · Q4 2026',
    stageTone: 'neutral',
    coverage: 15,
    headline: 'Architectural mapping in progress. Connector build starts Q4 2026.',
    highlights: [
      'Cloud Audit Logs · Security Command Center · Asset Inventory mapped',
      'Workload Identity Federation parity with AWS STS modeled',
      'BigQuery selected as the time-travel reconstruction backend',
    ],
    note: 'Awaiting design partner with material GCP estate before build.',
  },
];

const STAGE_TONE = {
  success: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  warning: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  neutral: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
};

const COVERAGE_TONE = {
  success: 'from-emerald-400 to-teal-400',
  warning: 'from-amber-400 to-amber-500',
  neutral: 'from-slate-500 to-slate-600',
};

export default function Integrations() {
  const { seed, setPartnerModalOpen } = useApp();
  if (!seed) return null;

  const tier1 = seed.integrations.filter((i) => i.tier === 1);
  const tier2 = seed.integrations.filter((i) => i.tier === 2);

  return (
    <div className="space-y-6" data-testid="integrations">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Governance · Integration Hub</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Evidence sources at a glance</h1>
        <p className="text-sm text-apex-neutral mt-1">Each connector contributes a specific evidence type. Coverage and confidence depend on which sources are live.</p>
      </div>

      <Group title="Tier 1 · Foundational" items={tier1} />
      <Group title="Tier 2 · Correlation" items={tier2} />

      <IntegrationsTimeline />

      {/* Multicloud Provider Maturity — what's GA today vs. on the roadmap */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        data-testid="provider-maturity"
        className="bg-apex-shell rounded-xl border border-slate-700 p-6"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-apex-lens mb-1.5">
              Cloud Provider Support
            </div>
            <h3 className="text-lg font-bold text-white leading-tight flex items-center gap-2">
              <Cloud className="w-4 h-4 text-apex-lens" />
              Multicloud maturity
            </h3>
            <p className="text-[12px] text-slate-400 mt-1.5 leading-relaxed max-w-2xl">
              We are intentionally AWS-deep first. Azure and GCP connectors follow a deliberate
              build order so each provider is governed to the same evidence standard before GA.
            </p>
          </div>
          <div className="text-[10px] font-mono text-slate-500">
            Updated Feb 2026
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PROVIDER_MATURITY.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + i * 0.06 }}
              data-testid={`provider-${p.id}`}
              className="bg-slate-800/60 rounded-lg p-4 border border-slate-700 flex flex-col"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{p.id.toUpperCase()}</div>
                  <h4 className="text-[13px] font-semibold text-white leading-snug mt-0.5">{p.name}</h4>
                </div>
                <span
                  className={`text-[10px] font-mono font-semibold uppercase tracking-wider px-2 py-0.5 rounded border whitespace-nowrap ${STAGE_TONE[p.stageTone]}`}
                  data-testid={`provider-${p.id}-stage`}
                >
                  {p.stage}
                </span>
              </div>

              {/* Coverage bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 mb-1">
                  <span>Connector coverage</span>
                  <span className="text-slate-200" data-testid={`provider-${p.id}-coverage`}>{p.coverage}%</span>
                </div>
                <div className="relative h-1.5 bg-slate-900 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${p.coverage}%` }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.4 + i * 0.06 }}
                    className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${COVERAGE_TONE[p.stageTone]}`}
                  />
                </div>
              </div>

              <p className="text-[12px] text-slate-300 leading-snug mb-3">{p.headline}</p>

              <ul className="space-y-1 mb-3">
                {p.highlights.map((h, j) => (
                  <li key={j} className="flex items-start gap-1.5 text-[11.5px] text-slate-400 leading-snug">
                    <span className="text-apex-lens mt-0.5 shrink-0">›</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-2 border-t border-slate-700 text-[10px] font-mono text-slate-500 italic">
                {p.note}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-700 text-[11px] text-slate-500 font-mono">
          GA = production-ready, all 9 evidence connector types live · Roadmap = scoped + design-partner aligned · Planned = architectural mapping done, build pending
        </div>
      </motion.div>

      {/* Pilot onboarding CTA — Design Partner program */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-apex-shell rounded-xl border border-slate-700 p-6"
        data-testid="pilot-cta"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-mono text-apex-lens mb-2">
              Design Partner Program
            </div>
            <h3 className="text-lg font-bold text-white leading-tight">
              Start a Design Partner Evaluation
            </h3>
            <p className="text-[13px] text-slate-400 mt-2 leading-relaxed max-w-xl">
              We are selecting 3–5 design partners to validate APEX Lens in real environments.
              Design partners receive early access, direct input on the roadmap, and a 90-day
              pilot at no cost in exchange for structured feedback.
            </p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PILOT_COLUMNS.map((col) => (
                <div key={col.label} className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{col.label}</div>
                  <ul className="space-y-1">
                    {col.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-300">
                        <span className="text-apex-lens mt-0.5 shrink-0">›</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setPartnerModalOpen(true)}
            data-testid="pilot-start-cta"
            className="inline-flex items-center gap-2 bg-apex-lens hover:bg-teal-500 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            <GitGraph className="w-4 h-4" />
            Request Design Partner Access
          </button>
          <span className="text-[11px] text-slate-500">
            3–5 partners only · Geneva cohort closing May 2026
          </span>
        </div>
      </motion.div>

      <EnterpriseReadiness />
    </div>
  );
}

function Group({ title, items }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-apex-ink mb-3">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it, i) => {
          const st = STATUS[it.status];
          const Icon = st.icon;
          return (
            <motion.div
              key={it.name}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`bg-white rounded-lg border ${st.bg} p-4`}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-semibold text-apex-ink">{it.name}</h4>
                <Icon className={`w-4 h-4 ${st.color}`} />
              </div>
              <p className="text-xs text-apex-neutral leading-relaxed mb-3">{it.contributes}</p>
              <div className="flex items-center justify-between text-[10px] font-mono text-apex-neutral">
                <span className={`uppercase tracking-wider font-semibold ${st.color}`}>{st.label}</span>
                {it.freshness_minutes !== null && (
                  <span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{it.freshness_minutes} min</span>
                )}
              </div>
              <div className="mt-2 text-[10px] text-apex-neutral">Coverage impact: <span className="capitalize">{it.coverage_impact}</span></div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
